import { Button, Field, Input } from "@bafe/ui";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { AuthShell } from "../../components/AuthShell";
import { ErrorPanel } from "../../components/ErrorPanel";
import { api } from "../../lib/api";
import { safeNextPath, useCompleteSignIn } from "../../lib/auth-flow";
import { fieldError, requiresTwoFactor } from "../../lib/errors";
import { setPendingSignIn } from "../../lib/stores";

export function SignIn() {
	const [searchParams] = useSearchParams();
	const next = safeNextPath(searchParams.get("next"));
	const completeSignIn = useCompleteSignIn();
	const navigate = useNavigate();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");

	const signIn = useMutation({
		mutationFn: () => api.login({ email: email.trim(), password }),
		onSuccess: (session) => completeSignIn(session, next ?? "/"),
		onError: (error) => {
			if (requiresTwoFactor(error)) {
				setPendingSignIn({ email: email.trim(), password });
				const query = next ? `?next=${encodeURIComponent(next)}` : "";
				void navigate(`/2fa${query}`);
			}
		},
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		signIn.mutate();
	}

	const showError = signIn.isError && !requiresTwoFactor(signIn.error);

	return (
		<AuthShell
			documentTitle="Key · Manage"
			title="Sign in to Key"
			description="Manage your domains, email accounts and storage plan."
			footer={
				<p className="text-center text-sm text-(--text-secondary)">
					For the mail app, open{" "}
					<a
						href="/webmail/"
						className="font-medium text-(--color-brand-700) underline underline-offset-2 hover:text-(--color-brand-800)"
					>
						Key Mail
					</a>{" "}
					and add it to your home screen from there.
				</p>
			}
		>
			<form className="flex flex-col gap-4" onSubmit={submit} noValidate>
				<Field label="Email or username" error={fieldError(signIn.error, "email")} required>
					{({ id, describedBy, invalid }) => (
						<Input
							id={id}
							type="text"
							name="email"
							value={email}
							required
							autoComplete="username"
							autoCapitalize="none"
							spellCheck={false}
							aria-describedby={describedBy}
							aria-invalid={invalid}
							onChange={(event) => setEmail(event.target.value)}
						/>
					)}
				</Field>

				<Field
					label="Password"
					error={fieldError(signIn.error, "password")}
					required
					action={
						<Link
							to="/forgot-password"
							className="text-xs text-(--color-brand-700) underline underline-offset-2"
						>
							Forgot password?
						</Link>
					}
				>
					{({ id, describedBy, invalid }) => (
						<Input
							id={id}
							type="password"
							name="password"
							value={password}
							required
							autoComplete="current-password"
							aria-describedby={describedBy}
							aria-invalid={invalid}
							onChange={(event) => setPassword(event.target.value)}
						/>
					)}
				</Field>

				{showError && <ErrorPanel error={signIn.error} />}

				<Button
					type="submit"
					variant="primary"
					fullWidth
					pending={signIn.isPending}
					pendingLabel="Signing in…"
				>
					Sign in
				</Button>
			</form>
		</AuthShell>
	);
}
